import React, { useState, useEffect, useRef } from 'react'
import { Head } from '@inertiajs/react'
import { Navigation, PrimaryButton } from '../../components'
import RenterOnboardingForm from '../../components/RenterOnboardingForm'
import TourRequestFrame from '../../components/iPhoneFrames/TourRequestFrame'
import ProfileFrame from '../../components/iPhoneFrames/ProfileFrame'
import AIConversationFrame from '../../components/iPhoneFrames/AIConversationFrame'
import {
    Box,
    Container,
    Typography,
    ThemeProvider,
    Grid,
    Fab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material'
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material'
import theme from '../../styles/theme'
import FlipTheScriptImage from '../../assets/Flip_the_script.png'
import { trackFeatureUsage } from '../../services/FeatureTrackingService'

export default function RentersPage() {
    const [showOnboardingForm, setShowOnboardingForm] = useState(false)
    const [showFloatingCTA, setShowFloatingCTA] = useState(false)
    const firstCTARef = useRef<HTMLDivElement>(null)

    // Intersection observer to detect when first CTA is in view
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Only show floating CTA if onboarding is not visible
                setShowFloatingCTA(!entry.isIntersecting && !showOnboardingForm)
            },
            {
                threshold: 0.1, // Trigger when 10% of the element is visible
                rootMargin: '-50px 0px 0px 0px' // Add some margin to trigger earlier
            }
        )

        if (firstCTARef.current) {
            observer.observe(firstCTARef.current)
        }

        return () => {
            if (firstCTARef.current) {
                observer.unobserve(firstCTARef.current)
            }
        }
    }, [showOnboardingForm])

    // Subtle pop-in animations for How it works
    const howItWorksRef = useRef<HTMLDivElement | null>(null)

    const StepNumber = ({ n }: { n: number }) => (
        <Box sx={{
            minWidth: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: '#fbbf24',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.125rem'
        }}>
            {n}
        </Box>
    )

    // Slide animations for frames when 10% visible
    const profileFrameRef = useRef<HTMLDivElement | null>(null)
    const aiConversationFrameRef = useRef<HTMLDivElement | null>(null)
    const tourFrameRef = useRef<HTMLDivElement | null>(null)
    const [slideProfile, setSlideProfile] = useState(false)
    const [slideAIConversation, setSlideAIConversation] = useState(false)
    const [slideTour, setSlideTour] = useState(false)

    useEffect(() => {
        const makeObserver = (cb: (v: boolean) => void) =>
            new IntersectionObserver(([entry]) => cb(entry.isIntersecting), { threshold: 0.1 })

        const obs1 = makeObserver(setSlideProfile)
        const obs2 = makeObserver(setSlideAIConversation)
        const obs3 = makeObserver(setSlideTour)

        if (profileFrameRef.current) obs1.observe(profileFrameRef.current)
        if (aiConversationFrameRef.current) obs2.observe(aiConversationFrameRef.current)
        if (tourFrameRef.current) obs3.observe(tourFrameRef.current)

        return () => {
            obs1.disconnect()
            obs2.disconnect()
            obs3.disconnect()
        }
    }, [])

    const handleStartHunting = () => {
        setShowOnboardingForm(true)
        setShowFloatingCTA(false) // Hide floating CTA when button is pressed
        // Smooth scroll to the form
        setTimeout(() => {
            document.getElementById('renter-onboarding-form')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }, 100)

        trackFeatureUsage(null, 'Start Hunting Button Clicked')
    }

    return (
        <ThemeProvider theme={theme}>
            <Head title="Let landlords come to you">
                <meta {...{ 'head-key': 'description' }} name="description" content="Flip the script for free" />
            </Head>
            <Navigation loginUrl="/renters/login" />

            {/* Section 1: Value Proposition */}
            <Box sx={{
                bgcolor: 'white',
                minHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                paddingTop: { xs: 10, sm: 13 },
                paddingBottom: { xs: 0, sm: 0 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '1.875rem', sm: '2.25rem', md: '3rem' },
                                        lineHeight: 1.2,
                                        color: 'grey.900',
                                        mb: 1.5
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            textAlign: 'left',
                                            paddingBottom: 1,
                                            mr: 1
                                        }}
                                    >
                                        Flip the script!
                                        <svg
                                            width="100%"
                                            height="8"
                                            viewBox="0 0 100 8"
                                            style={{
                                                position: 'absolute',
                                                bottom: '2px',
                                                left: '0',
                                                width: '100%',
                                                height: '8px'
                                            }}
                                        >
                                            <path
                                                d="M 0 6 Q 50 0 100 6"
                                                stroke="#fbbf24"
                                                strokeWidth="4"
                                                fill="none"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </Box>
                                    Let landlords come to you.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                                        color: 'grey.700',
                                        mb: 3,
                                        textAlign: 'left'
                                    }}
                                >
                                    Create your renter profile and let landlords request tours with you — you choose who to meet.
                                </Typography>

                                <Box ref={firstCTARef} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-start' } }}>
                                    <PrimaryButton
                                        variant="contained"
                                        size="large"
                                        onClick={handleStartHunting}
                                        sx={{
                                            bgcolor: 'grey.900',
                                            color: 'white',
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            width: 'auto',
                                            alignSelf: 'flex-start',
                                            '&:hover': {
                                                bgcolor: 'grey.800',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Create My Profile ✨
                                    </PrimaryButton>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.875rem',
                                            color: 'grey.800',
                                            mt: 1.5,
                                            ml: 2,
                                            textAlign: { xs: 'center', md: 'left' }
                                        }}
                                    >
                                        Free · Create in seconds
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Box
                                    component="img"
                                    src={FlipTheScriptImage}
                                    alt="Flip the script - Landlords come to you"
                                    sx={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        maxHeight: { xs: '300px', sm: '400px', md: '600px' },
                                        width: 'auto'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Section 2: Value Proposition */}
            <Box sx={{
                bgcolor: 'black',
                py: { xs: 6, md: 8 },
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '60px',
                    background: 'linear-gradient(to bottom, transparent, rgba(249, 250, 251, 0.1))',
                    pointerEvents: 'none'
                }
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            color: 'white',
                            mb: { xs: 2, md: 2.5 },
                            letterSpacing: { xs: '0.2px', sm: '0.25px', md: '0.3px' }
                        }}
                    >
                        Rental search on autopilot.
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                            color: 'grey.300',
                            mb: { xs: 2, md: 2.25 }
                        }}
                    >
                        Let landlords{' '}
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                position: 'relative',
                                paddingBottom: 1
                            }}
                        >
                            compete
                            <svg
                                width="100%"
                                height="8"
                                viewBox="0 0 100 8"
                                style={{
                                    position: 'absolute',
                                    bottom: '2px',
                                    left: '0',
                                    width: '100%',
                                    height: '8px'
                                }}
                            >
                                <path
                                    d="M 0 6 Q 50 0 100 6"
                                    stroke="#fbbf24"
                                    strokeWidth="4"
                                    fill="none"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </Box>
                        {' '}for you.
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.875rem',
                            color: 'white',
                            mt: { xs: 3.75, md: 4 }
                        }}
                    >
                        Free · Create in seconds
                    </Typography>
                </Container>
            </Box>


            {/* Section 3: How it works (subtle pop-in) */}
            <Box sx={{
                paddingTop: { xs: 2, md: 4 },
                paddingBottom: { xs: 2, md: 4 },
                bgcolor: 'grey.50',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '60px',
                    background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.05), transparent)',
                    pointerEvents: 'none'
                }
            }}>
                <Container maxWidth="lg" ref={howItWorksRef}>
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 700, fontSize: { xs: '1.875rem', sm: '2.25rem' }, color: 'grey.900', mt: 5, mb: 5, position: 'relative', zIndex: 1 }}>
                        As easy as 1,2,3
                    </Typography>

                    {/* Step 1 - Profile (text left on desktop, frame right) */}
                    <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <StepNumber n={1} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900', mb: 2.25 }}>
                                        Create your renter profile
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666', fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: { xs: '32ch', md: 'none' } }}>
                                        Tell us what you're looking for — it only takes seconds, and it helps us bring the right landlords to you.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box ref={profileFrameRef} sx={{
                                display: 'flex', justifyContent: 'center',
                                opacity: slideProfile ? 1 : 0,
                                transform: slideProfile ? 'translateX(0)' : 'translateX(24px)',
                                transition: 'opacity 420ms ease, transform 420ms ease',
                                mt: { xs: 2.5, md: 2.5 }
                            }}>
                                <ProfileFrame />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Step 2 - AI Conversation (frame left on desktop, text right) */}
                    <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
                        <Grid item xs={12} md={5} sx={{ order: { xs: 2, md: 1 } }}>
                            <Box ref={aiConversationFrameRef} sx={{
                                display: 'flex', justifyContent: 'center',
                                opacity: slideAIConversation ? 1 : 0,
                                transform: slideAIConversation ? 'translateX(0)' : 'translateX(-24px)',
                                transition: 'opacity 420ms ease, transform 420ms ease'
                            }}>
                                <AIConversationFrame />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <StepNumber n={2} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900', mb: 2.25 }}>
                                        We reach out to landlords for you
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666', fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: { xs: '32ch', md: 'none' } }}>
                                        We share your profile with matching landlords and invite them to request a tour.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Step 3 - Tour requests (text left on desktop, frame right) */}
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <StepNumber n={3} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900', mb: 2.25 }}>
                                        Landlords come to you — choose who you want to meet
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666', fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: { xs: '32ch', md: 'none' } }}>
                                        Accept or decline with a single tap.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box ref={tourFrameRef} sx={{
                                display: 'flex', justifyContent: 'center',
                                opacity: slideTour ? 1 : 0,
                                transform: slideTour ? 'translateX(0)' : 'translateX(24px)',
                                transition: 'opacity 420ms ease, transform 420ms ease'
                            }}>
                                <TourRequestFrame />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Section 4: Social Proof */}
            <Box sx={{
                bgcolor: '#101010',
                py: { xs: 6, md: 8 },
                overflow: 'hidden'
            }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.875rem', sm: '2.25rem', md: '2.5rem' },
                            color: 'white',
                            mb: { xs: 5.5, md: 6.5 },
                            textAlign: 'center'
                        }}
                    >
                        Loved by renters across Baltimore!
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.125rem' },
                            color: 'grey.300',
                            mb: 8,
                            textAlign: 'center'
                        }}
                    >
                    </Typography>

                    {/* Scrolling Row 1 - Left to Right */}
                    <Box sx={{
                        mb: { xs: 5, md: 5.5 },
                        overflow: 'hidden',
                        position: 'relative',
                        '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            width: { xs: '40px', md: '100px' },
                            zIndex: 1,
                            pointerEvents: 'none'
                        },
                        '&::before': {
                            left: 0,
                            background: 'linear-gradient(to right, #0A0A0A, transparent)'
                        },
                        '&::after': {
                            right: 0,
                            background: 'linear-gradient(to left, #0A0A0A, transparent)'
                        }
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 3,
                            animation: 'scrollLeft 50s linear infinite',
                            '@keyframes scrollLeft': {
                                '0%': { transform: 'translateX(0)' },
                                '100%': { transform: 'translateX(-50%)' }
                            },
                            width: 'max-content'
                        }}>
                            {[...Array(2)].map((_, idx) => (
                                <React.Fragment key={idx}>
                                    <Box sx={{
                                        bgcolor: '#161616',
                                        borderRadius: '16px',
                                        p: 3.75,
                                        width: '320px',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <Typography variant="body1" sx={{ fontSize: '0.9375rem', color: 'grey.200', lineHeight: 1.6 }}>
                                            "I'm a nurse with an abnormal schedule. Loved having help finding a home while I was working."
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: '#fbbf24',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem' }}>
                                                    DD
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.875rem' }}>
                                                    Drew D.
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.75rem' }}>
                                                    Baltimore, MD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        bgcolor: '#161616',
                                        borderRadius: '16px',
                                        p: 3.75,
                                        width: '320px',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <Typography variant="body1" sx={{ fontSize: '0.9375rem', color: 'grey.200', lineHeight: 1.6 }}>
                                            "Landlords actually came to me with tour requests. It was sick!"
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: '#fbbf24',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem' }}>
                                                    MJ
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.875rem' }}>
                                                    Michael J.
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.75rem' }}>
                                                    Baltimore, MD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        bgcolor: '#161616',
                                        borderRadius: '16px',
                                        p: 3.75,
                                        width: '320px',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <Typography variant="body1" sx={{ fontSize: '0.9375rem', color: 'grey.200', lineHeight: 1.6 }}>
                                            "Finally found a pet-friendly place that works for my budget"
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: '#fbbf24',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem' }}>
                                                    EC
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.875rem' }}>
                                                    Emily C.
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.75rem' }}>
                                                    Baltimore, MD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>

                    {/* Scrolling Row 2 - Right to Left */}
                    <Box sx={{
                        overflow: 'hidden',
                        position: 'relative',
                        '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            width: { xs: '40px', md: '100px' },
                            zIndex: 1,
                            pointerEvents: 'none'
                        },
                        '&::before': {
                            left: 0,
                            background: 'linear-gradient(to right, #0A0A0A, transparent)'
                        },
                        '&::after': {
                            right: 0,
                            background: 'linear-gradient(to left, #0A0A0A, transparent)'
                        }
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 3,
                            animation: 'scrollRight 55s linear infinite',
                            '@keyframes scrollRight': {
                                '0%': { transform: 'translateX(-50%)' },
                                '100%': { transform: 'translateX(0)' }
                            },
                            width: 'max-content'
                        }}>
                            {[...Array(2)].map((_, idx) => (
                                <React.Fragment key={idx}>
                                    <Box sx={{
                                        bgcolor: '#161616',
                                        borderRadius: '16px',
                                        p: 3.75,
                                        width: '320px',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <Typography variant="body1" sx={{ fontSize: '0.9375rem', color: 'grey.200', lineHeight: 1.6 }}>
                                            "Game changer! I got multiple tour requests within days."
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: '#fbbf24',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem' }}>
                                                    XJ
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.875rem' }}>
                                                    Xavier J.
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.75rem' }}>
                                                    Baltimore, MD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        bgcolor: '#161616',
                                        borderRadius: '16px',
                                        p: 3.75,
                                        width: '320px',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <Typography variant="body1" sx={{ fontSize: '0.9375rem', color: 'grey.200', lineHeight: 1.6 }}>
                                            "Easy to use. Simple. I had to move out in short notice and this made it less stressful."
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: '#fbbf24',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem' }}>
                                                    DP
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.875rem' }}>
                                                    Deandre P.
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.75rem' }}>
                                                    Baltimore, MD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        bgcolor: '#161616',
                                        borderRadius: '16px',
                                        p: 3.75,
                                        width: '320px',
                                        border: '1px solid',
                                        borderColor: 'rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        <Typography variant="body1" sx={{ fontSize: '0.9375rem', color: 'grey.200', lineHeight: 1.6 }}>
                                            "Best rental experience ever. Landlords were reaching out to me instead of the other way around."
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: '#fbbf24',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'black', fontWeight: 700, fontSize: '0.875rem' }}>
                                                    AT
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.875rem' }}>
                                                    Alex T.
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.75rem' }}>
                                                    Baltimore, MD
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Section 5: Comparison Table */}
            <Box sx={{
                bgcolor: 'grey.50',
                py: { xs: 6, md: 8 }
            }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.875rem', sm: '2.25rem', md: '2.5rem' },
                            color: 'grey.900',
                            mb: 1,
                            textAlign: 'center'
                        }}
                    >
                        How we're different from Zillow
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                            color: 'grey.500',
                            mb: 5,
                            textAlign: 'center'
                        }}
                    >
                        See what makes us unique
                    </Typography>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                            borderRadius: '16px',
                            overflow: 'auto',
                            border: 'none',
                            width: '100%',
                            display: 'block',
                            mt: 3,
                            mb: 3,
                            '@media (max-width: 600px)': {
                                display: 'block',
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }
                        }}
                    >
                        <Table sx={{
                            minWidth: { xs: 400, sm: 550 },
                            width: '100%',
                            display: 'table'
                        }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#1a1a1a' }}>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.5, sm: 1, md: 1.5 },
                                        whiteSpace: 'normal',
                                        width: { xs: '120px', sm: '140px', md: '160px' },
                                        maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        Feature
                                    </TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: '#1a1a1a',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        Heyy Property
                                    </TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: '#1a1a1a',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        Zillow
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow sx={{
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                        transform: 'scale(1.01)',
                                        transition: 'all 0.2s ease'
                                    },
                                    transition: 'all 0.2s ease'
                                }}>
                                    <TableCell component="th" scope="row" sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.5, sm: 1, md: 1.5 },
                                        whiteSpace: 'normal',
                                        width: { xs: '120px', sm: '140px', md: '160px' },
                                        maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        Landlords come to you
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: 'rgba(251, 191, 36, 0.1)',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CheckCircleIcon sx={{ color: '#fbbf24', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CancelIcon sx={{ color: 'error.main', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{
                                    bgcolor: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                        transform: 'scale(1.01)',
                                        transition: 'all 0.2s ease'
                                    },
                                    transition: 'all 0.2s ease'
                                }}>
                                    <TableCell component="th" scope="row" sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.5, sm: 1, md: 1.5 },
                                        whiteSpace: 'normal',
                                        width: { xs: '120px', sm: '140px', md: '160px' },
                                        maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        Landlords request to tour with you
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: 'rgba(251, 191, 36, 0.1)',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CheckCircleIcon sx={{ color: '#fbbf24', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CancelIcon sx={{ color: 'error.main', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                        transform: 'scale(1.01)',
                                        transition: 'all 0.2s ease'
                                    },
                                    transition: 'all 0.2s ease'
                                }}>
                                    <TableCell component="th" scope="row" sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.5, sm: 1, md: 1.5 },
                                        whiteSpace: 'normal',
                                        width: { xs: '120px', sm: '140px', md: '160px' },
                                        maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        You choose who to meet
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: 'rgba(251, 191, 36, 0.1)',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CheckCircleIcon sx={{ color: '#fbbf24', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CancelIcon sx={{ color: 'error.main', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{
                                    bgcolor: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                        transform: 'scale(1.01)',
                                        transition: 'all 0.2s ease'
                                    },
                                    transition: 'all 0.2s ease'
                                }}>
                                    <TableCell component="th" scope="row" sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.5, sm: 1, md: 1.5 },
                                        whiteSpace: 'normal',
                                        width: { xs: '120px', sm: '140px', md: '160px' },
                                        maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        AI-powered matching
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: 'rgba(251, 191, 36, 0.1)',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CheckCircleIcon sx={{ color: '#fbbf24', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CancelIcon sx={{ color: 'error.main', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{
                                    '&:hover': {
                                        bgcolor: 'grey.50',
                                        transform: 'scale(1.01)',
                                        transition: 'all 0.2s ease'
                                    },
                                    transition: 'all 0.2s ease'
                                }}>
                                    <TableCell component="th" scope="row" sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.5, sm: 1, md: 1.5 },
                                        whiteSpace: 'normal',
                                        width: { xs: '120px', sm: '140px', md: '160px' },
                                        maxWidth: { xs: '120px', sm: '140px', md: '160px' },
                                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        Completely free
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.25, sm: 2 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        bgcolor: 'rgba(251, 191, 36, 0.1)',
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CheckCircleIcon sx={{ color: '#fbbf24', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        py: { xs: 1.5, sm: 2.5 },
                                        px: { xs: 0.75, sm: 1, md: 1.5 },
                                        whiteSpace: 'nowrap',
                                        minWidth: { xs: '50px', sm: '60px', md: '80px' },
                                        maxWidth: { xs: '60px', sm: '70px', md: '90px' },
                                        width: { xs: '50px', sm: '60px', md: '80px' }
                                    }}>
                                        <CheckCircleIcon sx={{ color: 'grey.900', fontSize: { xs: 20, sm: 28, md: 32 } }} />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Container>
            </Box>

            {/* Section 6: FAQ */}
            <Box sx={{
                bgcolor: 'white',
                py: { xs: 6, md: 8 }
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.875rem', sm: '2.25rem', md: '2.5rem' },
                            color: 'grey.900',
                            mb: 1,
                            textAlign: 'center'
                        }}
                    >
                        Frequently Asked Questions
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                            color: 'grey.500',
                            mb: 5,
                            textAlign: 'center'
                        }}
                    >
                        Everything you need to know
                    </Typography>

                    <Box sx={{ mt: 4 }}>
                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    Is this really free?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                    Yes! No hidden fees ever. We won't ask for your credit card information.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    How long until I get tour requests?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                    About 2 - 3 days. Depends on your profile and what your looking for plus time of year..
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    What information do landlords see when I create a profile?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                   They will seee your first name and last initial. Your employer and position. If you have any pets and what your rental preferences are. We do not ask your income.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    How does tour scheduling work?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                    We make it very easy. You tell us when you're free and landlords pick tour times based on your schedule.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    What if I don't get any tour requests?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                    This is rare, however it does happen.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    Can I still search for listings on my own too?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                    Yes of course!
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion sx={{
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px !important',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 16px 0'
                            }
                        }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'grey.700' }} />}
                                sx={{
                                    px: 3,
                                    py: 2,
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200'
                                    }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
                                    What happens after I accept a tour request?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                                <Typography sx={{ color: 'grey.700', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                                    You have a tour scheduled. You go and check out the place. We hope you like it.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>

            {/* Renter Onboarding Form */}
            <RenterOnboardingForm
                isVisible={showOnboardingForm}
                id="renter-onboarding-form"
            />

            {/* Floating CTA Button */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 1000,
                    opacity: showFloatingCTA ? 1 : 0,
                    transform: showFloatingCTA ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                    pointerEvents: showFloatingCTA ? 'auto' : 'none'
                }}
            >
                <Fab
                    variant="extended"
                    color="primary"
                    onClick={handleStartHunting}
                    sx={{
                        bgcolor: 'grey.900',
                        borderColor: 'white',
                        border: '1px solid',
                        color: 'white',
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                            bgcolor: 'grey.800',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                        }
                    }}
                >
                    Create My Profile ✨
                </Fab>
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    bgcolor: 'grey.50',
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    py: 4,
                    mt: 8
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'grey.600',
                                fontSize: '0.875rem'
                            }}
                        >
                            © {new Date().getFullYear()} HeyyProperty Inc. All rights reserved.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'grey.500',
                                fontSize: '0.75rem'
                            }}
                        >
                            Made with ❤️
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    )
}
